import React, { useState } from 'react';
import { FilterBar } from '../components/FilterBar';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { useMetaTags } from '../hooks/useMetaTags';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { getDefaultProgram } from '../data/filterOptions';
import { SchoolListing } from '../types/api';
import { ExternalLinkIcon, ShieldCheckIcon, StarIcon, BuildingIcon, MapPinIcon } from 'lucide-react';
export function CollegesPage() {
  const [degreeLevel, setDegreeLevel] = useState<number>(3);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(1);
  const [selectedProgram, setSelectedProgram] = useState<number>(100);
  // Meta tags
  useMetaTags({
    title: 'Explore Accredited Online Colleges & Universities | DegreeCompare',
    description: 'Browse top-rated accredited online colleges and universities. Compare programs, read reviews, and find the right school for your online degree.',
    keywords: 'online colleges, accredited universities, online degree programs, best online schools, college directory, online education, distance learning',
    ogTitle: 'Explore Accredited Online Colleges & Universities',
    ogDescription: 'Browse and compare top-rated accredited online colleges. Find the right school for your online degree with detailed program information and reviews.'
  });
  const {
    geo
  } = useGeoLocation();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: selectedProgram,
    zipCode: geo.zipCode
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  return <div data-id="element-1535" data-mp="2cqmma4m1okrn">
      {/* Hero */}
      <section className="w-full bg-[#1a2332] text-white py-10 md:py-14 px-4 relative overflow-hidden" data-id="element-1536" data-mp="2qnjmuxrytglq">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1537" data-mp="2mmzoxrzki8yj"></div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-1538" data-mp="2trhdgn7na916">
          <div className="flex items-center gap-3 mb-4" data-id="element-1539" data-mp="2yny1lheugne9">
            <div className="bg-white/10 p-2 rounded-lg" data-id="element-1540" data-mp="2ymeilj1szy8s">
              <BuildingIcon className="w-6 h-6" data-id="element-1541" data-mp="33n2820adw70w" />
            </div>
            <span className="text-sm font-medium text-gray-300 uppercase tracking-wider" data-id="element-1542" data-mp="38ealr8iqyejv">
              College Directory
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3" data-id="element-1543" data-mp="3cfw59cpsdr31">
            Explore Accredited Online Colleges
          </h1>
          <p className="text-base text-gray-300 max-w-xl" data-id="element-1544" data-mp="3mds8zjge9jr4">
            Browse top-rated online colleges and universities. Click any school
            to learn more about their programs.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="w-full bg-white border-b border-gray-200 py-4 px-4 shadow-sm relative z-30" data-id="element-1545" data-mp="1s2dmrcmle2g4">
        <div className="max-w-7xl mx-auto" data-id="element-1546" data-mp="1x1hudytvbs4c">
          <FilterBar degreeLevel={degreeLevel} areaStudy={selectedAreaId} program={selectedProgram} onDegreeLevelChange={setDegreeLevel} onAreaStudyChange={handleAreaChange} onProgramChange={id => setSelectedProgram(id)} data-id="element-1547" data-mp="e4j13h9q7el1" />
        </div>
      </div>

      {/* College Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-10" data-id="element-1548" data-mp="cushnyor88t">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-id="element-1549" data-mp="arpm3cfpkmo">
            <p className="font-medium" data-id="element-1550" data-mp="x110mkutgiru">Error loading colleges</p>
            <p className="text-sm" data-id="element-1551" data-mp="t2l7dz856e5z">{error}</p>
          </div>}

        {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1552" data-mp="o1xh5frqm1ct">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse" data-id="element-1553" data-mp="j45eprtguwud">
                <div className="w-16 h-16 bg-gray-200 rounded-lg mb-4" data-id="element-1554" data-mp="38sxx1kui4gsx"></div>
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" data-id="element-1555" data-mp="33tus00eiuaky"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2" data-id="element-1556" data-mp="33vnx3b9hkmqu"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3" data-id="element-1557" data-mp="vjbwbw6dbfes"></div>
              </div>)}
          </div> : schools.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center" data-id="element-1558" data-mp="10dpta30ohulk">
            <BuildingIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-1559" data-mp="mm1hk0ucd854" />
            <p className="text-gray-600 font-medium" data-id="element-1560" data-mp="rk31enzldbhn">
              No colleges found for this selection.
            </p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1561" data-mp="1f9njl4nx84pv">
              Try adjusting your degree level or program filters.
            </p>
          </div> : <>
            <p className="text-sm text-gray-500 mb-6" data-id="element-1562" data-mp="15bhkeuti1cop">
              Showing {schools.length} college{schools.length !== 1 ? 's' : ''}{' '}
              for your selection
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1563" data-mp="3q0dzwrxkpb9a">
              {schools.map((school, index) => <CollegeCard key={`${school.mBrandID}-${index}`} school={school} data-id="element-1564" data-mp="3v11ol6ybxg0c" data-mpi={index} />)}
            </div>
          </>}
      </div>
    </div>;
}
function CollegeCard({
  school
}: {
  school: SchoolListing;
}) {
  return <a href={school.clickURL} target="_blank" rel="noopener noreferrer" className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all flex flex-col" data-id="element-1565" data-mp="3igmlpdoc9q0p">
      <div className="p-5 flex flex-col flex-grow" data-id="element-1566" data-mp="3rim7ejaati9z">
        {/* Logo + Name */}
        <div className="flex items-start gap-4 mb-4" data-id="element-1567" data-mp="33tbbpg7t2e2p">
          <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 p-1.5" data-id="element-1568" data-mp="2zqwv72rlzqel">
            <img src={school.imageURL} alt={`${school.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-1569" data-mp="3djcy83kifofi" />
          </div>
          <div className="min-w-0" data-id="element-1570" data-mp="38pi92km4yh91">
            <h3 className="text-base font-bold text-gray-900 group-hover:text-[#3b82f6] transition-colors leading-snug" data-id="element-1571" data-mp="2l1igrg96eisc">
              {school.mBrandName}
            </h3>
            {school.schoolHighlight && <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-id="element-1572" data-mp="k0av3pja3bez">
                <StarIcon className="w-3 h-3 fill-current" data-id="element-1573" data-mp="ox1b6cv3270e" />
                {school.schoolHighlight}
              </span>}
          </div>
        </div>

        {/* Description */}
        {school.aboutCollege ? <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-1574" data-mp="ynvvvcsokyjq">
            {school.aboutCollege}
          </p> : school.headContent ? <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-1575" data-mp="5zi1d70swkcx">
            {school.headContent}
          </p> : <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow" data-id="element-1576" data-mp="axjgjgv3vzj4">
            Accredited online programs with flexible scheduling options.
          </p>}

        {/* Accreditation */}
        {school.accreditation && <div className="flex items-start gap-2 mb-4 text-xs text-gray-500" data-id="element-1577" data-mp="1mgcgnv3sz4mq">
            <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1578" data-mp="1qea4oulu7koj" />
            <span className="line-clamp-1" data-id="element-1579" data-mp="3awm3apjq8onw">{school.accreditation}</span>
          </div>}

        {/* CTA */}
        <div className="pt-3 border-t border-gray-100 mt-auto flex items-center justify-between" data-id="element-1580" data-mp="36wu2x04hlotm">
          <span className="text-xs text-gray-500" data-id="element-1581" data-mp="3240wm7tde8ff">100% Online</span>
          <span className="text-sm font-bold text-[#e8594f] flex items-center gap-1.5 group-hover:gap-2 transition-all" data-id="element-1582" data-mp="2x7ai3ox7rkw4">
            Visit School <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-1583" data-mp="2s8z7hbz3h6cu" />
          </span>
        </div>
      </div>
    </a>;
}